import { Segment } from '@mairistem/composants';
import { Liste, ListeColumnType, ListeTable } from '@mairistem/liste';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { type ReactNode } from 'react';

import { LoadingContext, RecuContext, RegieContext } from '@/contexts';
import type { RecuListeItemType } from '@/types/recu';

import { formatRecusToListeItem, utils_liste } from '@/utils';
import { getRegieRegisseurFullName } from '@/utils/regisseur';
import { useRegieStore } from '@/zustand';
import RecusListeTableRow from './RecusListeTableRow';

interface RecusListeProps {
  /** Si la liste est intégré dans une page ou si elle est seule. */
  standAlone?: boolean;
  /**
   * Conservé pour compatibilité avec les appelants. Le préfiltrage par défaut sur le régisseur
   * connecté est désormais géré côté RecuContextProvider (filtres initiaux) ; la liste réaffiche
   * simplement les filtres mémorisés via getColumnFilterDefaults.
   */
  defaultFilteredOnRegisseur?: boolean;
  onOpen: (identifiant: number) => void;
}

const RecusListe = (props: RecusListeProps) => {
  const { standAlone = false, onOpen } = props;

  // #region Declaration

  const { total, recus, search, filters, setRecus, setFilters, setSorts, setSearch, setPage } =
    React.useContext(RecuContext);
  const { regies, selectedRegie } = React.useContext(RegieContext);
  const {
    recusLoadings: { recus: recusLoading },
  } = React.useContext(LoadingContext);

  const autonome = useRegieStore((state) => state.regie?.autonome);

  // Formate les données pour l'affichage dans la liste
  // avec seulement les data dont on a besoin
  const formatedRecus = React.useMemo<RecuListeItemType[]>(
    () => formatRecusToListeItem(recus),
    [recus],
  );

  // Liste des régisseurs de la régie sélectionnée
  const regisseurs = React.useMemo(() => {
    if (selectedRegie) {
      return _.find(regies, { identifiant: selectedRegie })?.regisseurs;
    }

    return [];
  }, [regies, selectedRegie]);

  // #endregion

  // #region Fonctions

  const handleSort = React.useCallback(
    (sort) => {
      setPage(1);
      setSorts(
        _.isEmpty(sort)
          ? // tri par défaut
            null
          : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
      setRecus([]);
    },
    [setRecus, setSorts, setPage],
  );

  const handleSearch = React.useCallback(
    async (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const handleFilter = React.useCallback(
    (f) => {
      // prevent la liste de trigger une deuxième fois l'appel à l'API
      // Si on essaye de réappliquer les filtres par défaut à tord
      if (utils_liste.areListeFiltersEqual(filters, f)) {
        return;
      }

      setPage(1);
      setFilters(
        _.map(f, (filter) => {
          if (filter.type === 'date') {
            return {
              ...filter,
              value: dayjs(filter.value).format('YYYY-MM-DD'),
            };
          }

          return filter;
        }),
      );
    },
    [filters, setPage, setFilters],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const renderRow = (item, invalidate, rowProps: Record<string, unknown>) => (
    <RecusListeTableRow item={item} rowProps={rowProps} onOpen={onOpen} autonome={autonome} />
  );

  // #endregion

  return (
    <Segment basic style={{ margin: 0, paddingTop: 0, height: '100%' }}>
      <Liste
        title=""
        onSearch={handleSearch}
        loading={recusLoading}
        searchable
        search={{
          value: search,
          placeholder: 'Rechercher un n° de reçu, un nom, un montant ou un n° de facture encaissée',
          clearable: true,
          autoFocus: true,
        }}
      >
        <Liste.Header> </Liste.Header>
        <Liste.Content>
          {/* -70px la hauteur de la barre de recherche */}
          <div style={{ height: standAlone ? 'calc(100% - 100px)' : 450 }}>
            <ListeTable
              count={_.isEmpty(formatedRecus) ? 0 : total}
              {...(standAlone && {
                showCount: true,
                countLabel: total > 1 ? 'reçus' : 'reçu',
              })}
              items={formatedRecus}
              loading={recusLoading}
              onFilter={handleFilter}
              onSort={handleSort}
              perPage={30}
              onLoad={handleLoad}
            >
              <ListeTable.Header>
                <ListeTable.Column
                  name="numero"
                  {...(!standAlone && { type: ListeColumnType.STRING })}
                  style={{ width: 120 }}
                  {...utils_liste.getColumnFilterDefaults('numero', filters)}
                >
                  N°
                </ListeTable.Column>
                <ListeTable.Column
                  name="payeur"
                  width={6}
                  {...(!standAlone && { type: ListeColumnType.STRING })}
                  sortable
                  {...utils_liste.getColumnFilterDefaults('payeur', filters)}
                >
                  Nom du Payeur
                </ListeTable.Column>
                <ListeTable.Column
                  name="dateCreation"
                  {...(!standAlone && { type: ListeColumnType.DATE })}
                  sortable
                  style={{ width: 100 }}
                  {...utils_liste.getColumnFilterDefaults('dateCreation', filters)}
                >
                  Date
                </ListeTable.Column>
                <ListeTable.Column name="adressePayeur" width={6}>
                  Adresse du payeur
                </ListeTable.Column>
                {!autonome && (
                  <ListeTable.Column
                    name="factures"
                    {...(!standAlone && { type: ListeColumnType.STRING })}
                    width={6}
                    {...utils_liste.getColumnFilterDefaults('factures', filters)}
                  >
                    N° de(s) facture(s) encaissée(s) par le reçu
                  </ListeTable.Column>
                )}
                <ListeTable.Column
                  name="montant"
                  {...(!standAlone && { type: ListeColumnType.NUMBER })}
                  sortable
                  style={{ width: 120 }}
                  {...utils_liste.getColumnFilterDefaults('montant', filters)}
                >
                  Montant
                </ListeTable.Column>
                <ListeTable.Column
                  name="dateAnnulation"
                  type={ListeColumnType.BOOLEAN}
                  style={{ width: 120 }}
                  filtersTextOperatorOverrides={{
                    RENSEIGNE: 'Annulé',
                    PAS_RENSEIGNE: 'Pas annulé',
                  }}
                  {...utils_liste.getColumnFilterDefaults('dateAnnulation', filters)}
                >
                  État reçu
                </ListeTable.Column>
                <ListeTable.Column
                  name="regisseur"
                  width={3}
                  type={ListeColumnType.STRING}
                  sortable
                  filtersDefaultActive="DANS_LISTE"
                  filtersValues={{
                    DANS_LISTE: _.map(regisseurs, getRegieRegisseurFullName),
                  }}
                  {...utils_liste.getColumnFilterDefaults('regisseur', filters)}
                >
                  Régisseur
                </ListeTable.Column>
              </ListeTable.Header>
              {renderRow as unknown as ReactNode}
            </ListeTable>
          </div>
        </Liste.Content>
      </Liste>
    </Segment>
  );
};

export default RecusListe;
